import { authActions } from 'actions/auth';

import {
  ESI_PATIENT_LOGIN_CHECK_CODE_RESULT,
  ESI_PATIENT_LOGIN_CONFIG_RESULT,
  SKIP_ON_BOARDING,
} from '../pages/ESI/PatientLogin/redux/constants';
import { authSet } from '../utils';

const loginResult = `${authActions.login}/result`;
const loginError = `${authActions.login}/error`;
const oauthLoginResult = `${authActions.oauth2Login}/result`;
const profileResult = `${authActions.profile}/result`;
const ssoLoginResult = `${authActions.ssoLogin}/result`;
const activateResult = 'user-activate/result';
const authStatus403 = 'auth/status/error';
const authStatus403UnSet = 'auth/status/unset';

const TwoFAResult = `${authActions.two_factor_auth}/result`;
const TwoFAError = `${authActions.two_factor_auth}/error`;
const verificationResult = `${authActions.verify_user}/result`;
const verificationError = `${authActions.verify_user}/error`;
const resolveLoginResult = `${authActions.resolveLogin}/result`;
const resolveLoginError = `${authActions.resolveLogin}/error`;

// Role switching action results
const roleSwitchingModeResult = `${authActions.roleSwitchingMode}/result`;
const switchableRolesResult = `${authActions.switchableRoles}/result`;
const currentRoleResult = `${authActions.currentRole}/result`;
const switchRoleResult = `${authActions.switchRole}/result`;
const resetRoleResult = `${authActions.resetRole}/result`;

const initialState = {
  recentlySwitchedRoles: [],
};

export const auth = (state = initialState, action) => {
  if (action.type.startsWith('auth/')) {
    switch (action.type) {
      case authActions.login:
        return {
          ...state,
          isLoading: true,
          loginErrors: undefined,
        };
      case loginResult:
      case TwoFAResult: {
        if (action.response.authentication_token) {
          if (action.response.version) {
            sessionStorage.setItem('version', action.response.version);
            sessionStorage.setItem('org', action.response?.organization?.name);
          }
          return {
            ...authSet(action.response),
            isLoading: false,
            is_doctor: action.response.is_doctor,
            role: action.response.role,
            loginErrors: undefined,
            reset_password: action.response.reset_password,
            organization: action.response.organization,
            ui_config: action.response.ui_config,
            permissions: readPermissions(action.response),
            org_config: action.response.org_config,
            org_requires_2fa_setup: action.response.org_requires_2fa_setup,
            user_two_fa_enabled: action.response.user_two_fa_enabled,
            preferences: action.response.preferences,
            config: action.response?.config,
            eula: action.response?.eula,
            recentlySwitchedRoles: state.recentlySwitchedRoles,
            native_role: action.response.native_role,
          };
        }
        // Handle force forgot password for inactive users
        if (action.response.force_forgot_password) {
          return {
            ...state,
            isLoading: false,
            force_forgot_password: true,
            force_forgot_password_method: action.response.force_forgot_password_method,
            force_forgot_password_reason: action.response.force_forgot_password_reason,
            reset_pending_token: action.response.reset_pending_token,
            phone_hint: action.response.phone_hint,
            email_hint: action.response.email_hint,
          };
        }
        // Handle 2FA - backend may use either two_factor_key or two_factor_token
        const twoFactorToken = action.response.two_factor_key || action.response.two_factor_token || action.response.requires_verification;
        if (twoFactorToken) {
          return {
            ...state,
            isLoading: false,
            two_factor_key: twoFactorToken,
            two_factor_method: action.response.two_factor_method,
            verification_token: action.response.verification_token,
            verification_method: action.response.verification_method,
            phone_hint: action.response.phone_hint,
            email_hint: action.response.email_hint,
            verified: action.response.verified,
            first_name: action.response.first_name,
            organization: action.response.organization,
          };
        }
        break;
      }
      case authActions.clear_two_factor_key:
      case verificationResult: {
        return {
          ...state,
          twoFAErrors: null,
          two_factor_key: null,
          two_factor_method: null,
          verification_token: null,
          verification_method: null,
          phone_hint: null,
          email_hint: null,
          isLoading: false,
        };
      }
      case authActions.clear_force_forgot_password: {
        return {
          ...state,
          force_forgot_password: false,
          reset_pending_token: null,
          phone_hint: null,
          email_hint: null,
        };
      }
      case authActions.two_factor_auth_resend_result: {
        return {
          ...state,
          isLoading: false,
          twoFAErrors: null,
          two_factor_key: action.response.two_factor_key || action.response.two_factor_token,
        };
      }
      case authActions.resolveLogin:
        return {
          ...state,
          ssoResolverLoading: true,
        };
      case resolveLoginResult:
      case resolveLoginError:
        return {
          ...state,
          ssoResolverLoading: false,
        };
      case authActions.two_factor_auth:
      case authActions.verify_user:
        return {
          ...state,
          isLoading: true,
          twoFAErrors: null,
        };
      case TwoFAError:
      case verificationError:
        return {
          ...state,
          twoFAErrors: action.response.data.error,
          isLoading: false,
        };

      case oauthLoginResult: {
        if (action.response.authentication_token) {
          return {
            ...authSet(action.response),
            loginErrors: undefined,
            origin: 'fhir',
            recentlySwitchedRoles: state.recentlySwitchedRoles,
          };
        }
        break;
      }
      case ssoLoginResult: {
        if (action.response.authentication_token) {
          return {
            ...authSet(action.response),
            loginErrors: undefined,
            context: action.response.context,
            origin: 'sso',
            recentlySwitchedRoles: state.recentlySwitchedRoles,
          };
        }
        break;
      }
      case authActions.profile:
        return {
          ...state,
          profileRequested: true,
        };

      case profileResult: {
        if (action.response.role) {
          sessionStorage.setItem('role', action.response.role);
        }

        return {
          ...state,
          is_doctor: action.response.is_doctor,
          role: action.response.role,
          organization: action.response.organization,
          permissions: readPermissions(action.response),
          preferences: action.response.preferences,
          ui_config: action.response.ui_config,
          org_config: action.response.org_config,
          config: action.response?.config,
          org_requires_2fa_setup: action.response.org_requires_2fa_setup,
          user_two_fa_enabled: action.response.user_two_fa_enabled,
          profile: { ...action.response },
          eula: action.response?.eula,
          native_role: action.response.native_role,
        };
      }
      case loginError: {
        return {
          ...state,
          authenticated: false,
          isLoading: false,
          loginErrors: action.response.data.error,
        };
      }
      case authActions.clearLoginForm:
        return {
          ...state,
          loginErrors: undefined,
        };
      case authStatus403:
        return {
          ...state,
          errors: action.response.data,
        };

      case authStatus403UnSet:
        return {
          ...state,
          errors: {},
        };

      // Role switching cases
      case roleSwitchingModeResult:
        return {
          ...state,
          roleSwitchingMode: action.response?.mode || 'none',
        };

      case switchableRolesResult:
        return {
          ...state,
          switchableRoles: action.response || [],
        };

      case currentRoleResult:
        return {
          ...state,
          currentRole: action.response?.current_role || null,
          originalRole: action.response?.original_role || null,
        };

      case switchRoleResult:
      case resetRoleResult:
        // After role switch/reset, store the new role info
        // The page will reload after this, so this is mostly for consistency
        return {
          ...state,
          currentRole: action.response?.current_role || null,
          originalRole: action.response?.original_role || null,
        };

      case authActions.add_recently_switched_role:
        let updatedRecentlySwitchedRoles;
        if (state.recentlySwitchedRoles?.length > 0) {
          const existingRoleIndex = state.recentlySwitchedRoles.findIndex(role => role.role_uuid === action.payload.role_uuid);
          if (existingRoleIndex !== -1) {
            // Role exists, increment counter
            const existingRole = state.recentlySwitchedRoles[existingRoleIndex];
            const updatedRole = { 
              ...action.payload, 
              switchCount: (existingRole.switchCount || 1) + 1 
            };
            updatedRecentlySwitchedRoles = state.recentlySwitchedRoles.map((role, index) => 
              index === existingRoleIndex ? updatedRole : role
            );
          } else {
            // New role, add with counter of 1
            updatedRecentlySwitchedRoles = [
              ...state.recentlySwitchedRoles,
              { ...action.payload, switchCount: 1 }
            ];
          }
        } else {
          // No existing roles, initialize with counter of 1
          updatedRecentlySwitchedRoles = [{ ...action.payload, switchCount: 1 }];
        }
        
        // Sort by switchCount in descending order
        updatedRecentlySwitchedRoles.sort((a, b) => (b.switchCount || 0) - (a.switchCount || 0));
        
        return {
          ...state,
          recentlySwitchedRoles: updatedRecentlySwitchedRoles,
        };
      default:
        return state;
    }
  } else if (action.type === activateResult && action.response.authentication_token) {
    return {
      ...authSet(action.response),
      loginErrors: undefined,
      recentlySwitchedRoles: state.recentlySwitchedRoles,
    };
  } else if (action.type === ESI_PATIENT_LOGIN_CHECK_CODE_RESULT && action.response.authentication_token) {
    return {
      ...authSet(action.response),
      isLoading: false,
      is_doctor: action.response.is_doctor,
      role: action.response.role,
      loginErrors: undefined,
      reset_password: action.response.reset_password,
      config: action.response?.config,
      permissions: readPermissions(action.response),
      preferences: action.response.preferences,
      ui_config: action.response.ui_config,
      org_config: action.response.org_config,
      eula: action.response?.eula,
      recentlySwitchedRoles: state.recentlySwitchedRoles,
      native_role: action.response.native_role,
    };
  } else if (action.type === ESI_PATIENT_LOGIN_CONFIG_RESULT) {
    return {
      ...state,
      config: action.response?.config,
    };
  } else if (action.type === SKIP_ON_BOARDING) {
    return {
      ...state,
      esi_is_portal_onboarding_tutorial_viewed: 'skip',
    };
  }

  return state;
};

const readPermissions = response => {
  const ui_access = response.ui_accesss ? response.ui_accesss : response.ui_access ? response.ui_access : undefined;
  return permissionsRecursiveSearch(ui_access);
};

const permissionsRecursiveSearch = (obj, path = '', results = []) => {
  let r = results;
  if (!obj) return r;

  const keys = (typeof obj === 'object') && Object.keys(obj);
  keys.forEach(key => {
    const newPath = `${path}${path ? '/' : ''}${key}`;

    const value = obj[key];
    if (typeof value === 'boolean' && value) {
      r.push(newPath);
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (typeof item === 'string') {
          r.push(`${newPath}/${item}`);
        } else if (typeof item === 'object') {
          r = permissionsRecursiveSearch(item, newPath, r);
        }
      });
    }
    if (typeof value === 'object') {
      r = permissionsRecursiveSearch(value, newPath, r);
    }
  });

  return r;
};
